import { onMounted, ref, watch, type Ref } from 'vue';
import { getSearchFormData } from '@/shared/api/getSearchFormData';
import type { Country, DepartureCity, DefaultCriteriaIndex } from '@/shared/model';

export interface UseSearchFormData {
  countries: Ref<Country[]>;
  cities: Ref<DepartureCity[]>;
  defaultCriteria: Ref<DefaultCriteriaIndex>;
  loading: Ref<boolean>;
}

export type SearchFormDataUrl = string | undefined | (() => string | undefined);

export function useSearchFormData(url?: SearchFormDataUrl): UseSearchFormData {
  const countries = ref<Country[]>([]);
  const cities = ref<DepartureCity[]>([]);
  const defaultCriteria = ref<DefaultCriteriaIndex>({});
  const loading = ref(false);

  const resolveUrl = (): string | undefined => (typeof url === 'function' ? url() : url);

  let requestId = 0;

  const load = async (): Promise<void> => {
    const id = ++requestId;
    loading.value = true;
    try {
      const data = await getSearchFormData(resolveUrl());
      if (id !== requestId) {
        return;
      }
      countries.value = data.countries;
      cities.value = data.cities;
      defaultCriteria.value = data.defaultCriteria;
    } finally {
      if (id === requestId) {
        loading.value = false;
      }
    }
  };

  onMounted(() => {
    load();
  });

  if (typeof url === 'function') {
    watch(url, () => {
      load();
    });
  }

  return { countries, cities, defaultCriteria, loading };
}
